import { getProvider } from "@aux0/connectors";
import type { ResponseStatus } from "@aux0/mock-data";
import { isInProgress } from "@aux0/mock-data";
import {
  ArrowLeft01Icon,
  Clock01Icon,
  Location01Icon,
  Video01Icon,
} from "@hugeicons/core-free-icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { AppLink } from "@/components/common/app-link";
import { ProviderMark, Tag } from "@/components/common/badges";
import { CopyBlock } from "@/components/common/copy-button";
import { Icon } from "@/components/common/icon";
import { Field, Page, PageTitle, Panel } from "@/components/common/page";
import { PageSkeleton } from "@/components/common/states";
import { Button } from "@/components/ui/button";
import { q } from "@/data/queries";
import { formatDateTime, formatDay, formatHourMin, initials } from "@/lib/format";
import { projectPath } from "@/lib/nav";
import { cn } from "@/lib/utils";

const RESPONSE: Record<ResponseStatus, { label: string; className: string }> = {
  accepted: { label: "Accepted", className: "border-ok/30 bg-ok-soft/60 text-ok" },
  declined: {
    label: "Declined",
    className: "border-stop/30 bg-stop-soft/70 text-stop",
  },
  tentative: {
    label: "Tentative",
    className: "border-wait/30 bg-wait-soft/60 text-wait",
  },
  needs_action: {
    label: "No answer",
    className: "border-warn/30 bg-warn-soft/70 text-warn",
  },
};

export const Route = createFileRoute("/p/$project/calendar/$eventId")({
  loader: async ({ context, params }) => {
    const event = await context.queryClient.ensureQueryData(
      q.calendarEvent(params.eventId),
    );
    if (!event) throw notFound();
    await Promise.all([
      context.queryClient.ensureQueryData(q.calendars()),
      context.queryClient.ensureQueryData(q.account(event.accountId)),
    ]);
    return { event };
  },
  pendingComponent: PageSkeleton,
  component: EventPage,
});

function EventPage() {
  const { project } = Route.useParams();
  const { event } = Route.useLoaderData();
  const { data: calendars } = useSuspenseQuery(q.calendars());
  const { data: account } = useSuspenseQuery(q.account(event.accountId));
  const calendar = calendars.find((candidate) => candidate.id === event.calendarId);
  const provider = getProvider(event.provider);
  const live = isInProgress(event);
  const readOnly = calendar?.access === "reader" || calendar?.access === "free_busy";

  const when = event.allDay
    ? `${formatDay(`${event.start}T00:00:00.000Z`)} — all day`
    : `${formatDay(event.start)} · ${formatHourMin(event.start)}–${formatHourMin(event.end)} UTC`;

  return (
    <Page>
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2 w-fit text-muted-foreground"
        asChild
      >
        <AppLink to={projectPath(project, "/calendar")}>
          <Icon icon={ArrowLeft01Icon} className="size-3.5" />
          Calendar
        </AppLink>
      </Button>

      <PageTitle
        leading={<ProviderMark provider={event.provider} size="lg" />}
        title={event.title}
        description={when}
        meta={
          <>
            {live ? (
              <Tag className="border-brand/30 bg-brand-soft/60 text-brand">
                <span aria-hidden className="size-1.5 rounded-full bg-brand-pink" />
                Happening now
              </Tag>
            ) : null}
            {event.status === "cancelled" ? (
              <Tag className="border-stop/30 bg-stop-soft/70 text-stop">Cancelled</Tag>
            ) : event.status === "tentative" ? (
              <Tag className="border-wait/30 bg-wait-soft/60 text-wait">Tentative</Tag>
            ) : null}
            {event.viaApi ? (
              <Tag mono className="border-brand/30 bg-brand-soft/60 text-brand">
                via API
              </Tag>
            ) : null}
            {event.recurringEventId ? <Tag mono>recurring</Tag> : null}
            <span className="mono-id">{event.id}</span>
          </>
        }
        actions={
          event.selfResponse === "needs_action" && !readOnly ? (
            <>
              <Button size="sm" disabled>
                Accept
              </Button>
              <Button variant="outline" size="sm" disabled>
                Decline
              </Button>
            </>
          ) : null
        }
      />

      {event.status === "cancelled" ? (
        <div
          role="alert"
          className="rounded-lg border border-stop/30 bg-stop-soft/40 px-4 py-3.5"
        >
          <h2 className="text-[13.5px] font-medium text-stop">
            The organiser cancelled this event
          </h2>
          <p className="mt-1 max-w-[76ch] text-[12.5px] leading-[1.55] text-ink-soft">
            Aux0 keeps it in the window as a tombstone rather than deleting the row, so
            a later sync cannot resurrect it and your own records can see that it was
            cancelled rather than simply vanishing.
          </p>
        </div>
      ) : null}

      {event.selfResponse === "needs_action" ? (
        <div className="rounded-lg border border-warn/30 bg-warn-soft/50 px-4 py-3.5">
          <h2 className="text-[13.5px] font-medium text-warn">
            {account?.handle ?? "This account"} has not answered
          </h2>
          <p className="mt-1 max-w-[76ch] text-[12.5px] leading-[1.55] text-ink-soft">
            An unanswered invitation still occupies the slot on the provider side, so a
            scheduling call will treat it as busy. Replying through the API fires{" "}
            <span className="font-mono">calendar.event_updated</span>.
          </p>
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-4">
          <Panel title="Details" bodyClassName="flex flex-col gap-3 p-4">
            <div className="flex flex-col gap-2.5 text-[13px]">
              <p className="flex items-start gap-2.5">
                <Icon
                  icon={Clock01Icon}
                  className="mt-px size-4 text-muted-foreground"
                />
                <span>
                  {when}
                  {event.recurrence ? (
                    <span className="mt-0.5 block font-mono text-[11.5px] text-muted-foreground">
                      {event.recurrence}
                    </span>
                  ) : null}
                </span>
              </p>
              {event.location ? (
                <p className="flex items-start gap-2.5">
                  <Icon
                    icon={Location01Icon}
                    className="mt-px size-4 text-muted-foreground"
                  />
                  <span>{event.location}</span>
                </p>
              ) : null}
              {event.conference ? (
                <p className="flex items-start gap-2.5">
                  <Icon
                    icon={Video01Icon}
                    className="mt-px size-4 text-muted-foreground"
                  />
                  <span className="min-w-0">
                    <span className="block">{event.conference.provider}</span>
                    <span className="block truncate font-mono text-[11.5px] text-muted-foreground">
                      {event.conference.url}
                    </span>
                    {event.conference.phone ? (
                      <span className="block text-[11.5px] text-muted-foreground">
                        Dial-in {event.conference.phone}
                      </span>
                    ) : null}
                  </span>
                </p>
              ) : null}
            </div>
            {event.description ? (
              <p className="max-w-[76ch] border-t pt-3 text-[13px] leading-[1.6] whitespace-pre-wrap">
                {event.description}
              </p>
            ) : null}
          </Panel>

          <Panel
            title={`Attendees (${event.attendees.length})`}
            description={
              event.guestsCanSeeOtherGuests
                ? "The organiser lets guests see each other"
                : "The organiser hides the guest list, so this is what the account can see"
            }
            bodyClassName="divide-y"
          >
            {event.attendees.length === 0 ? (
              <p className="px-4 py-5 text-[12.5px] text-muted-foreground">
                No attendees. An event with none is a block on the organiser's own
                calendar.
              </p>
            ) : (
              event.attendees.map((attendee) => {
                const response = RESPONSE[attendee.response];
                const isOrganizer = attendee.email === event.organizer.email;
                return (
                  <div
                    key={attendee.email}
                    className="flex flex-wrap items-center gap-3 px-4 py-2.5"
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "flex size-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold",
                        attendee.resource
                          ? "rounded-[6px] bg-muted text-muted-foreground"
                          : "bg-brand-soft text-brand",
                      )}
                    >
                      {initials(attendee.name ?? attendee.email)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] font-medium">
                        {attendee.name ?? attendee.email}
                      </span>
                      <span className="block truncate text-[11.5px] text-muted-foreground">
                        {attendee.email}
                      </span>
                    </span>
                    {isOrganizer ? <Tag mono>organiser</Tag> : null}
                    {attendee.optional ? <Tag mono>optional</Tag> : null}
                    {attendee.resource ? <Tag mono>room</Tag> : null}
                    <Tag className={response.className}>{response.label}</Tag>
                  </div>
                );
              })
            )}
          </Panel>
        </div>

        <div className="flex flex-col gap-4">
          <Panel title="Where it came from" bodyClassName="px-4 py-2">
            <dl>
              <Field label="Calendar">
                <span className="flex items-center gap-1.5">
                  {calendar ? (
                    <span
                      aria-hidden
                      className="size-2 shrink-0 rounded-[3px]"
                      style={{ backgroundColor: calendar.color }}
                    />
                  ) : null}
                  {calendar?.name ?? event.calendarId}
                </span>
              </Field>
              <Field label="Access">
                {calendar ? calendar.access.replace("_", " ") : "—"}
              </Field>
              <Field label="Provider">{provider?.name ?? event.provider}</Field>
              <Field label="Account">
                <AppLink
                  to={projectPath(project, `/accounts/${event.accountId}`)}
                  className="text-brand hover:underline"
                >
                  {account?.handle ?? event.accountId}
                </AppLink>
              </Field>
              <Field label="Created">{formatDateTime(event.createdAt)}</Field>
              <Field label="Updated">{formatDateTime(event.updatedAt)}</Field>
            </dl>
          </Panel>

          {event.reminders.length > 0 ? (
            <Panel
              title="Reminders"
              description="Minutes before the start that Aux0 fires a webhook"
              bodyClassName="flex flex-wrap gap-1.5 p-4"
            >
              {event.reminders.map((minutes) => (
                <Tag key={minutes} mono>
                  {minutes}m
                </Tag>
              ))}
            </Panel>
          ) : null}

          <Panel title="Fetch this event" bodyClassName="p-4">
            <CopyBlock
              value={`curl https://api.aux0.com/v1/calendar/events/${event.id} \\\n  -H "Authorization: Bearer $AUX0_API_KEY"`}
            />
          </Panel>
        </div>
      </div>
    </Page>
  );
}
